import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { BILL_STATUSES, type Bill, type BillStatus } from '@accounting/shared';
import { useBills } from '../../hooks/useBills';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/bills/')({
  component: BillsPage,
});

export function billStatusTag(bill: Pick<Bill, 'status' | 'isOverdue'>) {
  if (bill.status === 'paid') return <span className="tag ok">paid</span>;
  if (bill.status === 'void') return <span className="tag bad">void</span>;
  if (bill.isOverdue) return <span className="tag bad">past due</span>;
  if (bill.status === 'partial') return <span className="tag warn">partial</span>;
  return <span className="tag warn">open</span>;
}

function BillsPage() {
  const companyId = useActiveCompanyId();
  const [status, setStatus] = useState<BillStatus | ''>('');
  const { data: bills = [], isLoading, error } = useBills(companyId, {
    status: status || undefined,
  });

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Bills</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Bills</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Accounts payable</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/vendor-center" className="btn-secondary" style={{ textDecoration: 'none' }}>
            Vendor Center
          </Link>
          <label style={{ fontSize: 13 }}>
            Status{' '}
            <select value={status} onChange={(e) => setStatus(e.target.value as BillStatus | '')}>
              <option value="">All</option>
              {BILL_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <Link to="/bills/new" className="btn-primary" style={{ textDecoration: 'none' }}>
            + New bill
          </Link>
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load bills.</p>}

      {!isLoading && bills.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No bills yet.</p>
          <Link to="/bills/new" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Enter your first bill
          </Link>
        </div>
      )}

      {bills.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Vendor</th>
              <th>Ref</th>
              <th>Date</th>
              <th>Due</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Balance</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {bills.map((bill) => (
              <tr key={bill.id} style={{ opacity: bill.status === 'void' ? 0.6 : 1 }}>
                <td>{bill.billNumber}</td>
                <td>{bill.vendorName}</td>
                <td className="muted">{bill.vendorReference ?? '—'}</td>
                <td>{formatAccountingDate(bill.billDate)}</td>
                <td>{formatAccountingDate(bill.dueDate)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(bill.total)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(bill.outstandingAmount)}</td>
                <td>{billStatusTag(bill)}</td>
                <td>
                  <Link to="/bills/$id" params={{ id: bill.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
